import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Label } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { InlineEditableTextfield } from '@atlaskit/inline-edit';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	CONFLUENCE_PAGE_RENAME_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';
import type { ConfluencePageOption } from '@atlassian/rovo-platform-ui-components/base-page-selector/types';
import { ConfluencePageSelector } from '@atlassian/rovo-platform-ui-components/confluence-page-selector';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

const styles = cssMap({
	readOnlyTitle: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.075'),
	},
	readOnlyTitleWrapper: {
		marginTop: token('space.100'),
	},
});

export const filterConfluencePageRenameActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.ConfluencePageRename]>[] => {
	const filteredActions = actions.filter(assertIsConfluencePageRenameAction);
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.ConfluencePageRename]
	>[];
};

const assertIsConfluencePageRenameAction = (action: MessageAction): MessageAction => {
	if (action.key !== CONFLUENCE_PAGE_RENAME_KEY) {
		throw new Error('Invalid action key for ConfluencePageRenameAction');
	}
	return action;
};

export const ConfluencePageRenameAction = ({
	onCustomDataConfirm,
	actions,
	getStatus,
	getReadonly,
	cloudId,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.ConfluencePageRename>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const firstAction = actions[0];
	const { data } = firstAction;
	const [title, setTitle] = useState(data.title);
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const { getEntity } = useConfluenceEntities(cloudId);
	const [selectedEntity, setSelectedEntity] = useState<ConfluencePageOption | undefined>(undefined);
	const isDisabled = status !== 'idle' || readonly || !selectedEntity || !title;
	const isSubmitting = status === 'started';

	useEffect(() => {
		const fetchPage = async () => {
			if (data.documentId) {
				const entity = await getEntity(data.documentId, 'page');

				if (entity) {
					setSelectedEntity({
						label: entity.name,
						value: entity.id,
						type: 'page',
					});
				}
			}
		};

		fetchPage();
	}, [data.documentId, getEntity]);

	const handleSubmit = useCallback(() => {
		const mergedData = {
			...data,
			title,
			documentId: selectedEntity?.value,
			contentType: selectedEntity?.type ?? 'page',
		};

		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const plannedActions = actions.filter((action) => getStatus(action) === 'idle');

			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'confluencePageRenameRenderer',
				},
				confirmed_tools: {
					message_id: messageId,
					tools: plannedActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', mergedData)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onCustomDataConfirm(firstAction, mergedData);
	}, [
		onCustomDataConfirm,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		data,
		title,
		selectedEntity?.value,
		selectedEntity?.type,
		firstAction,
		actions,
		getStatus,
	]);

	return (
		<Stack space="space.250" testId="confluence-page-rename-action">
			<Text>
				{formatMessage(messages.actionMessage, {
					name: selectedEntity?.label ?? '',
					bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
				})}
			</Text>
			<ActionCardWrapper
				title={formatMessage(messages.confluencePageRenameActionCardTitle)}
				product="confluence"
			>
				<Stack space="space.100">
					<Inline space="space.100" alignBlock="center">
						<Heading size="xsmall">{formatMessage(messages.pageSelectorLabel)}</Heading>

						{!selectedEntity ? (
							<Skeleton
								height={30}
								width={200}
								borderRadius={token('radius.large')}
								testId="confluence-page-rename-page-selector-skeleton"
							/>
						) : (
							<ConfluencePageSelector
								cloudId={cloudId}
								testId="confluence-page-rename-page-selector"
								contentTypes={['page', 'blogPost']}
								isDisabled={isDisabled}
								onMenuOpen={() => {
									sendAnalyticsEvent({
										action: 'opened',
										actionSubject: 'dropdown',
										actionSubjectId: 'rovoConfluenceRenamePageSelector',
									});
								}}
								isRequired
								onChange={(option: ConfluencePageOption | null) => {
									if (option) {
										setSelectedEntity(option);
										sendAnalyticsEvent({
											action: 'selected',
											actionSubject: 'dropdown',
											actionSubjectId: 'rovoConfluenceRenamePageSelector',
										});
									}
								}}
								value={selectedEntity}
							/>
						)}
					</Inline>
					<Box>
						{isDisabled ? (
							<Box xcss={styles.readOnlyTitleWrapper}>
								<Label htmlFor="confluence-page-rename-title-text">
									{formatMessage(messages.titleLabel)}
								</Label>
								<Box id="confluence-page-rename-title-text" xcss={styles.readOnlyTitle}>
									{title}
								</Box>
							</Box>
						) : (
							<InlineEditableTextfield
								testId="confluence-page-rename-title-input-field"
								isRequired
								isCompact
								readViewFitContainerWidth
								defaultValue={title}
								label={formatMessage(messages.titleLabel)}
								onConfirm={(value) => {
									setTitle(value);
									sendAnalyticsEvent({
										action: 'edited',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluenceRename',
									});
								}}
								onCancel={() => {
									sendAnalyticsEvent({
										action: 'cancelled',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluenceRename',
									});
								}}
								placeholder={formatMessage(messages.titlePlaceholder)}
							/>
						)}
					</Box>
				</Stack>
				<ActionCardFooter>
					<Button
						appearance="primary"
						isDisabled={isDisabled}
						isLoading={isSubmitting}
						onClick={handleSubmit}
					>
						{formatMessage(messages.addLabelButton)}
					</Button>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};
